import { useEffect, useRef, useState, type ReactNode } from 'react';
import { LogOut, Plus, ShieldCheck, Users, X } from 'lucide-react';
import { Avatar } from './controls';
import { Tooltip } from './tooltip';
import { InRail } from './apprail';
import { msg, useT } from '../i18n';

/** One account on this device (D-028). */
export interface DeviceAccount {
  id: string;
  name: string;
  address: string;
  accent?: string;
  /** "personal" | "business" */
  space?: string;
  /** Unread conversations, when the app knows (Mail does). */
  unread?: number;
  /** Its session ran out: listed so it's one tap to sign in again, or to remove; never usable. */
  signedOut?: boolean;
}

export interface AccountSwitcherProps {
  current: DeviceAccount;
  /** Every account on this device, including the current one, in switcher order (g 1–5). */
  accounts: DeviceAccount[];
  onSwitch(account: DeviceAccount): void;
  /** Open this account in a new tab instead (apps that keep an account per tab). */
  onOpenInNewTab?(account: DeviceAccount): void;
  onAdd(): void;
  onSignOut(): void;
  onSignOutAll?(): void;
  /** A signed-out account was chosen: sign it in again (default: `onSwitch`). */
  onSignInAgain?(account: DeviceAccount): void;
  /** Forget a signed-out account on this device. */
  onRemove?(account: DeviceAccount): void;
  /** Bind the keyboard shortcut g then 1–5 (the account at that place in the list). */
  shortcuts?: boolean;
  /** Dugzi Account for the current account: "Manage your Dugzi Account", in the same place in every app (P2-4). */
  manageUrl?: string;
  /** App-specific items (settings, shortcuts, the app's data…), shown after the shared items. */
  children?: ReactNode;
  max?: number;
  testId?: string;
}

/**
 * The one order every Dugzi app lists accounts in, so g 1–5 means the same account everywhere and
 * doesn't change when you switch: personal accounts first, then work, each by address.
 */
export function orderAccounts<T extends DeviceAccount>(accounts: T[]): T[] {
  const rank = (a: DeviceAccount) => (a.space === 'business' ? 1 : 0);
  return [...accounts].sort((x, y) => rank(x) - rank(y) || x.address.localeCompare(y.address, undefined, { sensitivity: 'base' }));
}

/** Work accounts carry a small label everywhere they're shown, so the active space is never in doubt. */
export function SpaceBadge({ space }: { space?: string }) {
  const t = useT();
  return space === 'business' ? <span className="dz-space-badge">{t('ui.accounts.work')}</span> : null;
}

/**
 * Keyboard: g then 1–5 switches to the account at that place in `orderAccounts` order (the same in
 * every Dugzi app). Pass the accounts already ordered. Ignored while typing (inputs, editors, IME composition) and with modifier keys. The
 * pending "g" lives in a ref, so a list refresh between the two keys can't lose it.
 */
export function useAccountShortcuts(accounts: DeviceAccount[], currentId: string, onSwitch: (a: DeviceAccount) => void, enabled = true) {
  const latest = useRef({ accounts, currentId, onSwitch });
  latest.current = { accounts, currentId, onSwitch };
  const pendingG = useRef(0);
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      if (e.key === 'g') { pendingG.current = Date.now(); return; }
      if (Date.now() - pendingG.current < 1200 && /^[1-5]$/.test(e.key)) {
        pendingG.current = 0;
        const { accounts: list, currentId: cur, onSwitch: go } = latest.current;
        const a = list[Number(e.key) - 1];
        // `g 1`–`g 5` are the accounts wherever the switcher has shortcuts (D-049): the press is claimed even when it
        // names the current account or no account, so the app bar's own `g` + number never also opens an app.
        e.preventDefault();
        if (a && a.id !== cur) go(a);
      }
    };
    // Capture phase: runs before the app bar's listener on the same document, whatever order they were added in.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [enabled]);
}

/**
 * The account switcher: who you are right now, every other account on this device (with unread
 * counts where known, and "signed out" ones to sign in again), add another, sign out. Each account
 * wears its own accent colour. Keyboard: arrow keys, Home and End move, Enter chooses, Escape closes;
 * g then 1–5 switches from anywhere when `shortcuts` is on.
 */
export function AccountSwitcher({ current, accounts, onSwitch, onOpenInNewTab, onAdd, onSignOut, onSignOutAll, onSignInAgain, onRemove, shortcuts = false, manageUrl, children, max = 5, testId = 'account-menu' }: AccountSwitcherProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const list = orderAccounts(accounts.some((a) => a.id === current.id) ? accounts : [current, ...accounts]);
  const others = list.filter((a) => a.id !== current.id);
  const signedIn = list.filter((a) => !a.signedOut);
  const unreadElsewhere = others.reduce((n, a) => n + (a.signedOut ? 0 : a.unread ?? 0), 0);
  const choose = (a: DeviceAccount) => (a.signedOut ? (onSignInAgain ?? onSwitch)(a) : onSwitch(a));
  // g then 1–5 is the account at that place in every app (D-049), with or without the app bar; the bar keeps g then 6–9.
  useAccountShortcuts(list, current.id, choose, shortcuts);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const keys = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); ref.current?.querySelector<HTMLButtonElement>('.dz-switcher__btn')?.focus(); return; }
      if (e.key === 'Tab') { setOpen(false); return; }
      const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
      const at = items.indexOf(document.activeElement as HTMLElement);
      const to = e.key === 'ArrowDown' ? (at + 1) % items.length : e.key === 'ArrowUp' ? (at + items.length - 1) % items.length
        : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : -1;
      if (to < 0) return;
      items[to]?.focus();
      e.preventDefault();
    };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', keys);
    requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', keys); };
  }, [open]);
  const pick = (fn: () => void) => () => { setOpen(false); fn(); };
  const place = (a: DeviceAccount) => list.indexOf(a) + 1;
  return (
    <InRail slot="account"><div className="dz-switcher" ref={ref}>
      <button type="button" className="dz-switcher__btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        aria-label={`${t(current.space === 'business' ? msg('ui.accounts.button_work') : msg('ui.accounts.button'), { name: current.name, address: current.address })}${unreadElsewhere ? `. ${t('ui.accounts.unread_elsewhere', { count: unreadElsewhere })}` : ''}`} data-testid={testId}>
        <Avatar name={current.name} size={34} accent={current.accent} />
        {unreadElsewhere > 0 && <span className="dz-switcher__dot" aria-hidden />}
      </button>
      {open && (
        <div className="dz-menu dz-switcher__pop" role="menu" aria-label={t('ui.accounts.menu')}>
          <div className="dz-switcher__current">
            <Avatar name={current.name} size={44} accent={current.accent} />
            <div>
              <strong>{current.name} <SpaceBadge space={current.space} /></strong>
              <span className="dz-muted dz-small">{current.address}</span>
            </div>
          </div>
          {others.length > 0 && <>
            <hr className="dz-divider" />
            <div className="dz-menu__label">{t('ui.accounts.switch_to')}</div>
            {others.map((a) => (
              <div key={a.id} className={`dz-switcher__row${a.signedOut ? ' dz-switcher__row--out' : ''}`}>
                <button role="menuitem" className="dz-menu__item dz-switcher__account" onClick={pick(() => choose(a))}
                  data-testid={a.signedOut ? 'sign-in-again' : 'switch-account'} data-address={a.address}
                  aria-keyshortcuts={shortcuts && place(a) <= 5 ? `g ${place(a)}` : undefined}
                  aria-label={a.signedOut ? t('ui.accounts.signed_out_label', { name: a.name, address: a.address }) : undefined}>
                  <Avatar name={a.name} size={30} accent={a.signedOut ? undefined : a.accent} />
                  <span className="dz-switcher__who"><span>{a.name} <SpaceBadge space={a.space} /></span><small className="dz-muted">{a.signedOut ? <>{t('ui.accounts.signed_out')} · <span className="dz-switcher__again">{t('ui.accounts.sign_in_again')}</span></> : a.address}</small></span>
                  {!a.signedOut && !!a.unread && <span className="dz-switcher__count" aria-label={t('ui.accounts.unread', { count: a.unread })}>{a.unread > 99 ? '99+' : a.unread}</span>}
                  {!a.signedOut && shortcuts && place(a) <= 5 && <kbd className="dz-switcher__key" aria-hidden>g {place(a)}</kbd>}
                </button>
                {!a.signedOut && onOpenInNewTab && <Tooltip content={t('ui.accounts.new_tab')}><button role="menuitem" className="dz-switcher__tab" onClick={pick(() => onOpenInNewTab(a))} aria-label={t('ui.accounts.new_tab_label', { address: a.address })}>↗</button></Tooltip>}
                {a.signedOut && onRemove && <Tooltip content={t('ui.accounts.remove')}><button role="menuitem" className="dz-switcher__tab" onClick={pick(() => onRemove(a))} aria-label={t('ui.accounts.remove_label', { address: a.address })} data-testid="remove-account"><X aria-hidden size={16} /></button></Tooltip>}
              </div>
            ))}
          </>}
          <hr className="dz-divider" />
          {signedIn.length < max
            ? <button role="menuitem" className="dz-menu__item" onClick={pick(onAdd)} data-testid="add-account"><Plus aria-hidden />{t('ui.accounts.add')}</button>
            : <p className="dz-menu__label">{t('ui.accounts.max', { max })}</p>}
          {manageUrl && <a role="menuitem" className="dz-menu__item" href={manageUrl} data-testid="manage-account"><ShieldCheck aria-hidden />{t('ui.accounts.manage')}</a>}
          {children && <><hr className="dz-divider" />{children}</>}
          <hr className="dz-divider" />
          <button role="menuitem" className="dz-menu__item" onClick={pick(onSignOut)} data-testid="sign-out"><LogOut aria-hidden />{signedIn.length > 1 ? t('ui.accounts.sign_out_of', { address: current.address }) : t('ui.accounts.sign_out')}</button>
          {signedIn.length > 1 && onSignOutAll && <button role="menuitem" className="dz-menu__item" onClick={pick(onSignOutAll)} data-testid="sign-out-all"><Users aria-hidden />{t('ui.accounts.sign_out_all')}</button>}
        </div>
      )}
    </div></InRail>
  );
}

// Which account this browser tab uses (D-028): moved to a React-free module (F-26 C6) so the shared
// API client can use it; same keys, same API.
export { tabAccount } from '../tab-account';

/**
 * Tell this app's other tabs that the accounts changed (signed out, removed, added), so they
 * refresh their switcher at once instead of at their next request. Same origin only, by design.
 */
export function announceAccountsChanged(app: string) {
  try { const c = new BroadcastChannel(`dz.${app}.accounts`); c.postMessage('changed'); c.close(); } catch { /* old browsers: next request notices */ }
}

export function useAccountsChanged(app: string, onChange: () => void) {
  const fn = useRef(onChange);
  fn.current = onChange;
  useEffect(() => {
    let c: BroadcastChannel | null = null;
    try { c = new BroadcastChannel(`dz.${app}.accounts`); c.onmessage = () => fn.current(); } catch { /* unsupported */ }
    return () => c?.close();
  }, [app]);
}
